import { createAutoParCells } from "./autoParPresentation.ts";
// AUTOPAR1 (docs/build-briefs/AUTOPAR1-automatic-par-levels.md; PRD P0.12; DEC-412; design/mockups/autopar-1..6): Settings >
// Inventory items, Automatic par levels. Three pieces, one module:
//
//   * the SETTINGS cards (Items tab): the switch, the order pace of each vendor, and the three Calculation steppers;
//   * the PAR LEVELS tab: one vendor's items with on hand, par now, used per week, auto par, its change tag, Accept for a
//     held change, and the lock;
//   * two sheets, built at runtime (check:chrome-ratchet refuses a new scrim in index.html): the order pace prompt, which
//     is also the Edit and Change pace sheet, and the Keep or Put back prompt shown on switch off.
//
// Over GET and PUT /tenants/:tenantId/locations/:locationId/auto-par. The location is one the server lists (this device's location
// first), never typed. Every control acts over its own call and draws what the server holds afterwards. Visible to a
// session that may view stock levels; every control is off for one that may not manage them (resolved permissions, never a
// role name). Admin surface, every control 44px or taller. All text via textContent. Nothing is typed: steppers and chips.

import {
  getAutoPar,
  getAutoParItems,
  listTenantLocations,
  postAutoParAccept,
  postAutoParRecalculate,
  putAutoParItemLock,
  putAutoParSettings,
  putVendorOrderPace,
  type AutoParItemDto,
  type AutoParOverviewDto,
  type AutoParVendorDto,
  type TenantLocationNameDto,
  type VendorDto,
} from "./api.ts";
import {
  BUFFER_RANGE_BASIS_POINTS,
  BUFFER_STEP_BASIS_POINTS,
  HISTORY_RANGE_WEEKS,
  LEAD_RANGE_DAYS,
  LOOKBACK_RANGE_WEEKS,
  PACE_PRESET_WEEKS,
  WEEKDAY_SHORT,
  WEEKDAY_VALUES,
  changeTag,
  coverageDays,
  draftFromVendor,
  figure,
  historyLabel,
  paceDays,
  paceLine,
  recalculationResultText,
  percentLabel,
  perWeekUnit,
  presetLabel,
  presetOf,
  stepAmount,
  stepLead,
  withUnit,
  type PaceDraft,
  type WeekdayValue,
} from "./autoParModel.ts";
import { sortByName } from "./nameSort.ts";

export interface AutoParInventorySection {
  inventoryLocationId(): string | undefined;
  /** The name of the location the Items list is showing (BULKVENDOR1's confirmation sentence). */
  inventoryLocationName(): string | undefined;
  inventoryPending(): boolean;
  inventoryMatchesVendor(productId: string): boolean;
  inventoryRow(productId: string): AutoParItemDto | undefined;
  /** BULKEDIT1: the name of the vendor the item orders from at this location, "Not set" when it has none, undefined while unread. */
  inventoryVendorName(productId: string): string | undefined;
  inventoryCells(productId: string): { auto: HTMLElement; used: HTMLElement; lock: HTMLElement } | undefined;
}

export interface AutoParDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  /** The session holds inv.stock_state.view (the read of this section). */
  canView: () => boolean;
  /** The session holds admin.liquor_product.manage (the switch, the steppers, the lock, Accept, Update now). */
  canManage: () => boolean;
  /** The session holds inv.vendor.manage (a vendor's order pace). */
  canManageVendors: () => boolean;
  /** The location this device belongs to, when it is set up as one. */
  deviceLocation: () => { id: string; name: string } | undefined;
  /** True while the session this section serves is still the live one. */
  isCurrent: () => boolean;
  unifiedCatalog?: { available: () => boolean; changed: () => void; locationChanged: () => void };
}

export interface AutoParParts {
  /** The group panel the Items and Par levels tabs live in. */
  group: HTMLElement;
  /** The Items and Par levels tab strip. */
  tabs: HTMLElement;
  /** The switch, order pace and Calculation cards, shown on the Items tab. */
  settings: HTMLElement;
  /** The Par levels tab. */
  levels: HTMLElement;
  /** Where the two sheets are put: the admin screen itself, so a sheet opened from Vendors shows although the Inventory
   * group (which owns the tabs) is not the open one. */
  sheetHost: HTMLElement;
  inventoryActions?: HTMLElement;
  /** LOCSET1 (B13): the Setup menu of Inventory items. Automatic par levels and Recalculate are added to it; Import is
   * already in it. When absent they are drawn beside Add item as before. */
  inventorySetupMenu?: HTMLElement;
  /** LOCSET1 (B13): where the Vendor filter sits, with the other list filters under the search. */
  vendorFilter?: HTMLElement;
}

export interface PaceVendor {
  id: string;
  name: string;
  paceDays: number | null;
  orderWeekday: string | null;
  leadDays: number;
}

export interface AutoParSection {
  refresh(): Promise<void>;
  reset(): void;
  /** Opens the order pace sheet for one vendor from another screen (Vendors). `onSaved` gets the saved vendor row. */
  openVendorPace(vendor: PaceVendor, onSaved: (vendor: VendorDto) => void): void;
  /** Shows the Par levels tab on one vendor (Vendors, See par levels). */
  showLevels(vendorId?: string): Promise<void>;
}

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string | null,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tag);
  if (className !== null) created.className = className;
  if (text !== undefined) created.textContent = text;
  return created;
}

function messageOf(body: unknown): string {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === "string" && message.length > 0
    ? message
    : "Something went wrong. Try again.";
}

const ID = "admin-autopar";

type Tab = "items" | "levels";

export function mountAutoPar(parts: AutoParParts, deps: AutoParDeps): AutoParSection & AutoParInventorySection {
  let locations: { id: string; name: string }[] = [];
  let locationId: string | undefined;
  let overview: AutoParOverviewDto | undefined;
  let tab: Tab = "items";
  let levelsVendorId: string | undefined;
  let items: AutoParItemDto[] = [];
  let itemsLoaded = false;
  let busy = false;
  let pendingWrite: symbol | undefined;
  let errorLine = "";
  let levelsError = "";
  let loadSequence = 0;
  let sheetScrim: HTMLElement | undefined;
  let settingsOpen = false;
  let recalculationText = "";
  let recalculationOperation: symbol | undefined;
  const recalculationFeedback = node("span", "ap-recalculation-status");
  recalculationFeedback.setAttribute("role", "status");
  recalculationFeedback.setAttribute("aria-live", "polite");
  const metric = (value: string | null): string => value === null ? "–" : value;
  const updatedText = (lastRunAt: string | null): string => {
    const stamp = lastRunAt === null ? null : new Date(lastRunAt);
    return stamp === null || Number.isNaN(stamp.getTime()) ? "Updated –" : `Updated ${stamp.toLocaleString()}`;
  };
  const usesUnifiedCatalog = (): boolean => deps.unifiedCatalog?.available() === true;

  parts.tabs.hidden = true;
  parts.settings.hidden = true;
  parts.levels.hidden = true;

  function reset(): void {
    loadSequence += 1;
    settingsOpen = false;
    parts.inventoryActions?.querySelector("[data-inventory-par-actions]")?.remove();
    locations = [];
    locationId = undefined;
    overview = undefined;
    tab = "items";
    levelsVendorId = undefined;
    items = [];
    itemsLoaded = false;
    busy = false;
    pendingWrite = undefined;
    errorLine = "";
    levelsError = "";
    recalculationText = "";
    recalculationOperation = undefined;
    recalculationFeedback.textContent = "";
    closeSheet();
    parts.group.dataset["autoparTab"] = "items";
    for (const part of [parts.tabs, parts.settings, parts.levels]) {
      part.replaceChildren();
      part.hidden = true;
    }
    if (usesUnifiedCatalog()) deps.unifiedCatalog?.changed();
  }

  async function loadLocations(sequence: number): Promise<void> {
    const listed = await listTenantLocations(deps.apiBaseUrl(), deps.tenantId());
    if (sequence !== loadSequence || !deps.isCurrent()) return;
    const device = deps.deviceLocation();
    if (listed.status === 200 && Array.isArray(listed.body.locations)) {
      const found = listed.body.locations as TenantLocationNameDto[];
      locations = found.map((siteLocation) => ({ id: siteLocation.id, name: siteLocation.name }));
      const preferred =
        (device !== undefined &&
          locations.find((siteLocation) => siteLocation.id === device.id)?.id) ||
        found.find((siteLocation) => siteLocation.isMain)?.id ||
        locations[0]?.id;
      locationId =
        locationId !== undefined && locations.some((siteLocation) => siteLocation.id === locationId)
          ? locationId
          : preferred;
      return;
    }
    locations = device === undefined ? [] : [device];
    locationId = device?.id;
  }

  async function load(): Promise<boolean> {
    if (!deps.canView()) {
      reset();
      return false;
    }
    const sequence = (loadSequence += 1);
    if (locations.length === 0) {
      await loadLocations(sequence);
      if (sequence !== loadSequence || !deps.isCurrent()) return false;
      if (usesUnifiedCatalog()) deps.unifiedCatalog?.locationChanged();
    }
    if (locationId === undefined) {
      reset();
      return false;
    }
    const result = await getAutoPar(deps.apiBaseUrl(), deps.tenantId(), locationId);
    if (sequence !== loadSequence || !deps.isCurrent()) return false;
    if (result.status === 200 && typeof result.body.settings?.enabled === "boolean") {
      overview = result.body;
      pickLevelsVendor();
      render();
      if (tab === "levels" || usesUnifiedCatalog()) return await loadItems();
      return true;
    }
    if (result.status === 403 || result.status === 404) {
      reset();
      return false;
    }
    overview = undefined;
    errorLine = `Could not load Automatic par levels. ${messageOf(result.body)}`;
    render();
    return false;
  }

  function pickLevelsVendor(): void {
    const vendors = levelVendors();
    if (usesUnifiedCatalog()) {
      if (levelsVendorId !== undefined && levelsVendorId !== "none" && !vendors.some((vendor) => vendor.id === levelsVendorId)) levelsVendorId = undefined;
    } else if (levelsVendorId === undefined || !vendors.some((vendor) => vendor.id === levelsVendorId)) {
      levelsVendorId = vendors[0]?.id;
    }
  }

  /** The vendors the Par levels tab offers: those with at least one item at this location. */
  function levelVendors(): AutoParVendorDto[] {
    return sortByName(
      (overview?.vendors ?? []).filter((vendor) => usesUnifiedCatalog() || vendor.itemCount > 0),
      (vendor) => vendor.name,
    );
  }

  async function loadItems(): Promise<boolean> {
    if (locationId === undefined || (!usesUnifiedCatalog() && levelsVendorId === undefined)) {
      items = [];
      itemsLoaded = true;
      renderLevels();
      return locationId !== undefined;
    }
    const sequence = (loadSequence += 1);
    itemsLoaded = false;
    renderLevels();
    const result = await getAutoParItems(
      deps.apiBaseUrl(),
      deps.tenantId(),
      locationId,
      !usesUnifiedCatalog() ? levelsVendorId : undefined,
    );
    if (sequence !== loadSequence || !deps.isCurrent()) return false;
    if (result.status === 200 && Array.isArray(result.body.items)) {
      items = result.body.items;
      levelsError = "";
    } else {
      items = [];
      levelsError = messageOf(result.body);
    }
    itemsLoaded = true;
    renderLevels();
    return result.status === 200 && Array.isArray(result.body.items);
  }

  // -------------------------------------------------------------------
  // Drawing
  // -------------------------------------------------------------------

  function render(): void {
    renderTabs();
    renderSettings();
    renderLevels();
  }

  function renderTabs(): void {
    parts.tabs.replaceChildren();
    if (usesUnifiedCatalog()) {
      parts.tabs.hidden = true;
      parts.group.dataset["autoparTab"] = "items";
      return;
    }
    parts.tabs.hidden = overview === undefined;
    parts.group.dataset["autoparTab"] = tab;
    if (overview === undefined) return;
    parts.tabs.setAttribute("role", "tablist");
    parts.tabs.setAttribute("aria-label", "Inventory items");
    for (const option of [
      { key: "items" as const, label: "Items" },
      { key: "levels" as const, label: "Par levels" },
    ]) {
      const button = node("button", "ap-tab", option.label);
      button.type = "button";
      button.setAttribute("role", "tab");
      button.setAttribute("aria-selected", String(option.key === tab));
      button.dataset["testid"] = `${ID}-tab-${option.key}`;
      button.addEventListener("click", () => void selectTab(option.key));
      parts.tabs.append(button);
    }
  }

  async function selectTab(next: Tab): Promise<void> {
    if (next === tab) return;
    tab = next;
    levelsError = "";
    renderTabs();
    renderSettings();
    renderLevels();
    if (next === "levels") {
      itemsLoaded = false;
      renderLevels();
      await loadItems();
    }
  }

  function locationChips(into: HTMLElement): void {
    if (locations.length <= 1) return;
    const chips = node("div", "rt-locations");
    chips.setAttribute("role", "group");
    chips.setAttribute("aria-label", "Which location");
    for (const siteLocation of sortByName(locations, (listed) => listed.name)) {
      const chip = node("button", "tablets-chip", siteLocation.name);
      chip.type = "button";
      chip.dataset["testid"] = `${ID}-location`;
      chip.dataset["locationId"] = siteLocation.id;
      chip.setAttribute("aria-pressed", String(siteLocation.id === locationId));
      chip.addEventListener("click", () => {
        if (siteLocation.id === locationId || busy) return;
        locationId = siteLocation.id;
        errorLine = "";
        recalculationText = "";
        recalculationFeedback.textContent = "";
        items = [];
        itemsLoaded = false;
        loadSequence += 1;
        overview = undefined;
        if (usesUnifiedCatalog()) deps.unifiedCatalog?.locationChanged();
        render();
        void load();
      });
      chips.append(chip);
    }
    into.append(chips);
  }

  function renderSettings(): void {
    parts.settings.replaceChildren();
    const current = overview;
    parts.settings.hidden = current === undefined && errorLine === "";
    if (current === undefined) {
      if (errorLine !== "") parts.settings.append(errorNode(errorLine));
      return;
    }
    const mayManage = deps.canManage() && !busy;
    const settingsLocationId = locationId;
    let settingsTarget = parts.settings;
    if (usesUnifiedCatalog()) {
      // LOCSET1 (B13): the settings are opened from the Setup menu and stay out of the way of the list until then.
      if (!settingsOpen) {
        parts.settings.hidden = true;
        return;
      }
      const summary = node("div", "ap-unified-settings-summary");
      summary.append(node("h3", "section-title", "Automatic par levels"), node("span", "ap-sub", current.settings.enabled ? "On" : "Off"));
      const edit = node("button", "btn-secondary btn-compact", settingsOpen ? "Done" : "Edit");
      edit.type = "button";
      edit.setAttribute("aria-expanded", String(settingsOpen));
      edit.addEventListener("click", () => { settingsOpen = !settingsOpen; renderSettings(); });
      summary.append(edit);
      parts.settings.append(summary);
      settingsTarget = node("div", "ap-unified-settings-body");
      settingsTarget.hidden = !settingsOpen;
      const guard = (event: Event): void => {
        if (settingsLocationId !== locationId || current !== overview || !deps.isCurrent()) { event.preventDefault(); event.stopImmediatePropagation(); }
      };
      settingsTarget.addEventListener("click", guard, true);
      settingsTarget.addEventListener("keydown", guard, true);
      parts.settings.append(settingsTarget);
    } else locationChips(parts.settings);

    // Card 1: the switch.
    const switchCard = node("section", "bd-card ap-card");
    switchCard.dataset["testid"] = `${ID}-card`;
    const row = node("div", "ap-switch-row");
    const text = node("div", "ap-switch-text");
    text.append(node("h3", "bd-card-title", "Automatic par levels"));
    if (current.settings.enabled) {
      const updated = node("span", "ap-sub", updatedText(current.settings.lastRunAt));
      updated.dataset["testid"] = `${ID}-updated`;
      text.append(updated);
    }
    const state = node("span", "ap-switch-state", current.settings.enabled ? "On" : "Off");
    state.dataset["testid"] = `${ID}-state`;
    const toggle = node("button", "ap-switch");
    toggle.type = "button";
    toggle.setAttribute("role", "switch");
    toggle.setAttribute("aria-checked", String(current.settings.enabled));
    toggle.setAttribute("aria-label", "Automatic par levels");
    toggle.dataset["testid"] = `${ID}-switch`;
    toggle.disabled = !mayManage;
    toggle.append(node("span", "ap-switch-knob"));
    toggle.addEventListener("click", () => {
      if (current.settings.enabled) openOffSheet();
      else void switchOn();
    });
    row.append(text, state, toggle);
    switchCard.append(row);
    if (errorLine !== "") switchCard.append(errorNode(errorLine));
    settingsTarget.append(switchCard);

    // Card 2: the order pace of each vendor.
    if (current.vendors.length > 0) {
      const paceCard = node("section", "bd-card ap-card");
      paceCard.dataset["testid"] = `${ID}-paces`;
      paceCard.append(node("h3", "bd-card-title", "Order pace by vendor"));
      const list = node("div", current.settings.enabled ? "ap-list" : "ap-list ap-dim");
      for (const vendor of sortByName(current.vendors, (listed) => listed.name)) {
        list.append(paceRow(vendor));
      }
      paceCard.append(list);
      settingsTarget.append(paceCard);
    }

    // Card 3: the Calculation steppers, shown while the switch is on.
    if (current.settings.enabled) {
      const calc = node("section", "bd-card ap-card");
      calc.dataset["testid"] = `${ID}-calculation`;
      calc.append(node("h3", "bd-card-title", "Calculation"));
      const settings = current.settings;
      const list = node("div", "ap-list");
      list.append(
        stepperRow({
          testId: "lookback",
          label: "Look back",
          value: String(settings.lookbackWeeks),
          unit: "weeks",
          canDecrease: settings.lookbackWeeks > LOOKBACK_RANGE_WEEKS.min,
          canIncrease: settings.lookbackWeeks < LOOKBACK_RANGE_WEEKS.max,
          mayManage,
          step: (direction) => {
            const lookbackWeeks = settings.lookbackWeeks + direction;
            // The history needed can never be longer than the look back.
            return void saveSettings({
              lookbackWeeks,
              ...(settings.historyWeeks > lookbackWeeks ? { historyWeeks: lookbackWeeks } : {}),
            });
          },
        }),
        stepperRow({
          testId: "buffer",
          label: "Safety buffer",
          value: percentLabel(settings.bufferBasisPoints),
          unit: "%",
          canDecrease: settings.bufferBasisPoints > BUFFER_RANGE_BASIS_POINTS.min,
          canIncrease: settings.bufferBasisPoints < BUFFER_RANGE_BASIS_POINTS.max,
          mayManage,
          step: (direction) =>
            void saveSettings({
              bufferBasisPoints: settings.bufferBasisPoints + direction * BUFFER_STEP_BASIS_POINTS,
            }),
        }),
        stepperRow({
          testId: "history",
          label: "History needed",
          value: String(settings.historyWeeks),
          unit: "weeks",
          canDecrease: settings.historyWeeks > HISTORY_RANGE_WEEKS.min,
          canIncrease:
            settings.historyWeeks < HISTORY_RANGE_WEEKS.max &&
            settings.historyWeeks < settings.lookbackWeeks,
          mayManage,
          step: (direction) =>
            void saveSettings({ historyWeeks: settings.historyWeeks + direction }),
        }),
      );
      calc.append(list);
      settingsTarget.append(calc);
    }
  }

  function errorNode(message: string): HTMLElement {
    const error = node("p", "admin-error", message);
    error.setAttribute("role", "alert");
    error.dataset["testid"] = `${ID}-error`;
    return error;
  }

  function paceRow(vendor: AutoParVendorDto): HTMLElement {
    const row = node("div", "ap-row");
    row.dataset["testid"] = `${ID}-pace-row`;
    row.dataset["vendorId"] = vendor.id;
    const text = node("div", "ap-row-text");
    text.append(node("span", "ap-row-title", vendor.name));
    const line = node(
      "span",
      vendor.paceDays === null ? "ap-sub ap-na" : "ap-sub",
      paceLine(vendor),
    );
    line.dataset["testid"] = `${ID}-pace-line`;
    text.append(line);
    row.append(text);
    if (overview?.canSetPace === true && deps.canManageVendors()) {
      const button = node(
        "button",
        vendor.paceDays === null ? "btn-primary ap-row-button" : "btn-secondary ap-row-button",
        vendor.paceDays === null ? "Set pace" : "Edit",
      );
      button.type = "button";
      button.dataset["testid"] = `${ID}-pace-edit`;
      button.disabled = busy;
      button.setAttribute(
        "aria-label",
        `${vendor.paceDays === null ? "Set" : "Edit"} the order pace of ${vendor.name}`,
      );
      button.addEventListener("click", () =>
        openPaceSheet({ vendors: [vendor], index: 0, flow: false }),
      );
      row.append(button);
    }
    return row;
  }

  function stepperRow(options: {
    testId: string;
    label: string;
    value: string;
    unit: string;
    canDecrease: boolean;
    canIncrease: boolean;
    mayManage: boolean;
    step: (direction: 1 | -1) => void;
  }): HTMLElement {
    const row = node("div", "ap-row");
    row.dataset["testid"] = `${ID}-${options.testId}-row`;
    row.append(node("span", "ap-row-title", options.label));
    row.append(
      stepper({
        testId: `${ID}-${options.testId}`,
        label: options.label,
        value: options.value,
        unit: options.unit,
        canDecrease: options.canDecrease && options.mayManage,
        canIncrease: options.canIncrease && options.mayManage,
        onStep: options.step,
      }),
    );
    return row;
  }

  function stepper(options: {
    testId: string;
    label: string;
    value: string;
    unit: string;
    canDecrease: boolean;
    canIncrease: boolean;
    onStep: (direction: 1 | -1) => void;
  }): HTMLElement {
    const wrap = node("div", "ap-stepper");
    const minus = node("button", "ap-step-button", "−");
    minus.type = "button";
    minus.setAttribute("aria-label", `Less ${options.label}`);
    minus.dataset["testid"] = `${options.testId}-minus`;
    minus.disabled = !options.canDecrease;
    minus.addEventListener("click", () => options.onStep(-1));
    const value = node("span", "ap-step-value");
    value.dataset["testid"] = `${options.testId}-value`;
    value.append(node("b", null, options.value), node("span", null, options.unit));
    const plus = node("button", "ap-step-button", "+");
    plus.type = "button";
    plus.setAttribute("aria-label", `More ${options.label}`);
    plus.dataset["testid"] = `${options.testId}-plus`;
    plus.disabled = !options.canIncrease;
    plus.addEventListener("click", () => options.onStep(1));
    wrap.append(minus, value, plus);
    return wrap;
  }

  function renderLevels(): void {
    if (usesUnifiedCatalog()) { renderUnifiedToolbar(); return; }
    parts.levels.replaceChildren();
    const current = overview;
    parts.levels.hidden = tab !== "levels" || (current === undefined && recalculationText === "");
    if (tab !== "levels") return;
    if (current === undefined) {
      recalculationFeedback.textContent = recalculationText;
      parts.levels.append(recalculationFeedback);
      if (levelsError !== "") parts.levels.append(errorNode(levelsError));
      return;
    }
    const mayManage = deps.canManage() && !busy;
    locationChips(parts.levels);

    const vendors = levelVendors();
    const bar = node("div", "ap-bar");
    const chips = node("div", "ap-vendor-chips");
    chips.setAttribute("role", "group");
    chips.setAttribute("aria-label", "Vendor");
    for (const vendor of vendors) {
      const chip = node("button", "ap-chip", vendor.name);
      chip.type = "button";
      chip.dataset["testid"] = `${ID}-vendor`;
      chip.dataset["vendorId"] = vendor.id;
      chip.setAttribute("aria-pressed", String(vendor.id === levelsVendorId));
      chip.addEventListener("click", () => {
        if (vendor.id === levelsVendorId || busy) return;
        levelsVendorId = vendor.id;
        itemsLoaded = false;
        renderLevels();
        void loadItems();
      });
      chips.append(chip);
    }
    bar.append(chips, node("span", "ap-spacer"));
    const updated = node(
      "span",
      "ap-updated",
      updatedText(current.settings.lastRunAt),
    );
    updated.dataset["testid"] = `${ID}-levels-updated`;
    bar.append(updated);
    const update = node("button", "btn-primary btn-compact", recalculationOperation === undefined ? "Recalculate auto par" : "Recalculating...");
    update.type = "button";
    update.dataset["testid"] = `${ID}-update-now`;
    update.disabled = !mayManage || !current.settings.enabled;
    update.addEventListener("click", () => void updateNow());
    bar.append(update);
    parts.levels.append(bar, recalculationFeedback);
    recalculationFeedback.textContent = recalculationText;
    if (levelsError !== "") parts.levels.append(errorNode(levelsError));

    const card = node("section", "bd-card ap-card ap-table-card");
    const table = node("div", "ap-table");
    table.setAttribute("role", "table");
    table.dataset["testid"] = `${ID}-table`;
    const head = node("div", "ap-table-head");
    head.setAttribute("role", "row");
    for (const [label, numeric] of [
      ["Item", false],
      ["On hand", true],
      ["Par now", true],
      ["Used", false],
      ["Auto par", true],
      ["Lock", true],
    ] as const) {
      const cell = node("span", numeric ? "ap-num" : null, label);
      cell.setAttribute("role", "columnheader");
      head.append(cell);
    }
    table.append(head);
    if (vendors.length === 0) {
      table.append(node("p", "ap-empty", "No vendors yet. Add a vendor to set an order pace."));
    } else if (!itemsLoaded) {
      table.append(node("p", "ap-empty", "Items are loading."));
    } else {
      for (const item of items) table.append(itemRow(item, mayManage));
    }
    card.append(table);
    parts.levels.append(card);
  }

  function renderUnifiedToolbar(): void {
    parts.levels.replaceChildren();
    parts.inventoryActions?.querySelector("[data-inventory-par-actions]")?.remove();
    parts.inventorySetupMenu?.querySelectorAll("[data-inventory-par-actions]").forEach((entry) => entry.remove());
    parts.vendorFilter?.replaceChildren();
    parts.levels.hidden = overview === undefined && recalculationText === "" && levelsError === "";
    recalculationFeedback.textContent = recalculationText;
    if (overview !== undefined) {
      locationChips(parts.levels);
      const label = node("label", "admin-filter-label ap-vendor-filter", "Vendor");
      const picker = node("select", "field-input");
      picker.setAttribute("aria-label", "Inventory vendor");
      picker.dataset["settingExempt"] = "list filter";
      for (const vendor of [{ id: "", name: "All vendors" }, ...levelVendors(), { id: "none", name: "No vendor" }]) {
        const option = node("option", null, vendor.name); option.value = vendor.id; picker.append(option);
      }
      picker.value = levelsVendorId ?? "";
      picker.disabled = busy;
      const pickerOverview = overview;
      const pickerLocationId = locationId;
      picker.addEventListener("change", () => {
        if (pickerOverview !== overview || pickerLocationId !== locationId || !deps.isCurrent()) return;
        levelsVendorId = picker.value === "" ? undefined : picker.value; deps.unifiedCatalog?.changed();
      });
      label.append(picker);
      // LOCSET1 (B13): the Vendor filter sits with the other filters under the search, not above the list's own controls.
      if (parts.vendorFilter !== undefined) parts.vendorFilter.append(label);
      else { const tools = node("div", "ap-unified-tools"); tools.append(label); parts.levels.append(tools); }

      const toolbarLocationId = locationId;
      const toolbarSequence = loadSequence;
      // LOCSET1 (B13, DEC-532): rare setup (the automatic par settings, Recalculate) sits behind the Setup menu with Import.
      const settingsButton = node("button", "btn-secondary btn-compact", `Automatic par levels: ${overview.settings.enabled ? "On" : "Off"}`);
      settingsButton.type = "button";
      settingsButton.dataset["testid"] = `${ID}-open-settings`;
      settingsButton.addEventListener("click", () => {
        settingsOpen = true;
        renderSettings();
        parts.settings.scrollIntoView({ block: "nearest" });
      });
      const update = node("button", "btn-secondary btn-compact", recalculationOperation === undefined ? "Recalculate auto par" : "Recalculating..."); update.type = "button";
      update.dataset["testid"] = `${ID}-update-now`;
      update.disabled = !deps.canManage() || busy || !overview.settings.enabled;
      update.addEventListener("click", () => { if (toolbarLocationId === locationId && toolbarSequence === loadSequence && deps.isCurrent()) void updateNow(); });
      if (parts.inventorySetupMenu !== undefined) {
        for (const entry of [settingsButton, update]) entry.dataset["inventoryParActions"] = "true";
        parts.inventorySetupMenu.prepend(settingsButton, update);
      } else {
        const actions = node("span", "ap-unified-actions");
        actions.dataset["inventoryParActions"] = "true";
        actions.append(node("span", "ap-updated", updatedText(overview.settings.lastRunAt)), update);
        (parts.inventoryActions ?? parts.levels).prepend(actions);
      }
      parts.levels.append(recalculationFeedback);
      if (levelsError !== "") parts.levels.append(errorNode(levelsError));
    }
    if (overview === undefined) {
      parts.levels.append(recalculationFeedback);
      if (levelsError !== "") parts.levels.append(errorNode(levelsError));
    }
    deps.unifiedCatalog?.changed();
  }

  function itemRow(item: AutoParItemDto, mayManage: boolean): HTMLElement {
    const itemLocationId = locationId;
    const itemSequence = loadSequence;
    const itemIsCurrent = (): boolean =>
      itemLocationId === locationId &&
      itemSequence === loadSequence &&
      deps.isCurrent();
    const row = node(
      "div",
      item.locked ? "ap-table-row ap-locked" : "ap-table-row",
    );
    row.setAttribute("role", "row");
    row.dataset["testid"] = `${ID}-item`;
    row.dataset["productId"] = item.liquorProductId;

    const name = node("div", "ap-item");
    const title = node("span", "ap-item-name", item.name);
    title.dataset["testid"] = `${ID}-item-name`;
    name.append(title, node("span", "ap-sub", item.size ?? "-"));

    const onHand = node("span", "ap-num", metric(item.onHand));
    onHand.dataset["testid"] = `${ID}-on-hand`;
    const parNow = node("span", "ap-num");
    parNow.dataset["testid"] = `${ID}-par-now`;
    parNow.append(node("b", null, metric(item.parNow)));

    let unavailableReason: string | undefined;
    if (item.autoPar === null || item.usedPerWeek === null) {
      unavailableReason =
        item.eligible === false
          ? "Automatic par is unavailable for this item."
          : item.status === "insufficient_history"
            ? item.historyDays === 0
              ? "No sales history is available for this item."
              : "There is not enough sales history for this item."
            : item.status === "no_vendor"
              ? "This item has no preferred vendor."
              : item.status === "no_pace"
                ? "The vendor's order pace is not set."
                : item.status === "out_of_stock"
                  ? "There is not enough in-stock usage history."
                  : item.status === "no_count_unit"
                    ? "This item has no count unit."
                    : item.status === "mixed_units"
                      ? "This item's usage has incompatible units."
                      : "Automatic par data is unavailable.";
    }
    const { auto, used, lock } = createAutoParCells({
      name: item.name,
      used: item.usedPerWeek,
      history: historyLabel(item.historyDays),
      insufficientHistory: item.status === "insufficient_history",
      unit: perWeekUnit(item.countUnit),
      variant: item.tag,
      tag: changeTag(item),
      auto: metric(item.autoPar),
      held: figure(item.heldPar),
      mayManage,
      locked: item.locked,
      ...(unavailableReason === undefined ? {} : { unavailableReason }),
      autoMissing: item.autoPar === null,
      onAccept: () => {
        if (itemIsCurrent()) void acceptItem(item);
      },
      onLock: () => {
        if (itemIsCurrent()) void toggleLock(item);
      },
    });

    row.append(name, onHand, parNow, used, auto, lock);
    return row;
  }

  // -------------------------------------------------------------------
  // Acts
  // -------------------------------------------------------------------

  function beginWrite(): symbol {
    const operation = Symbol();
    pendingWrite = operation;
    busy = true;
    return operation;
  }

  async function finishWrite<T>(operation: symbol, sequence: number, request: Promise<T>): Promise<T> {
    try {
      return await request;
    } finally {
      if (pendingWrite === operation) {
        pendingWrite = undefined;
        busy = false;
        if (sequence !== loadSequence && deps.isCurrent()) render();
      }
    }
  }

  async function saveSettings(body: Parameters<typeof putAutoParSettings>[3]): Promise<void> {
    if (locationId === undefined || busy) return;
    const sequence = loadSequence;
    const operation = beginWrite();
    errorLine = "";
    render();
    const result = await finishWrite(operation, sequence, putAutoParSettings(deps.apiBaseUrl(), deps.tenantId(), locationId, body));
    if (sequence !== loadSequence || !deps.isCurrent()) return;
    if (result.status !== 200) errorLine = messageOf(result.body);
    // Draw what the server holds, not what was tapped.
    await load();
  }

  async function switchOn(): Promise<void> {
    if (locationId === undefined || busy) return;
    const sequence = loadSequence;
    const operation = beginWrite();
    errorLine = "";
    render();
    const result = await finishWrite(operation, sequence, putAutoParSettings(deps.apiBaseUrl(), deps.tenantId(), locationId, {
      enabled: true,
    }));
    if (sequence !== loadSequence || !deps.isCurrent()) return;
    if (result.status !== 200) errorLine = messageOf(result.body);
    await load();
    // The prompt: how often does the location order from each vendor. Only for a person who may set it.
    if (
      result.status === 200 &&
      overview !== undefined &&
      overview.canSetPace &&
      deps.canManageVendors() &&
      overview.vendors.length > 0
    ) {
      openPaceSheet({
        vendors: sortByName(overview.vendors, (listed) => listed.name),
        index: 0,
        flow: true,
      });
    }
  }

  async function switchOff(restore: boolean): Promise<void> {
    if (locationId === undefined || busy) return;
    const sequence = loadSequence;
    const operation = beginWrite();
    errorLine = "";
    closeSheet();
    render();
    const result = await finishWrite(operation, sequence, putAutoParSettings(deps.apiBaseUrl(), deps.tenantId(), locationId, {
      enabled: false,
      restore,
    }));
    if (sequence !== loadSequence || !deps.isCurrent()) return;
    if (result.status !== 200) errorLine = messageOf(result.body);
    tab = "items";
    await load();
  }

  async function updateNow(): Promise<void> {
    if (locationId === undefined || busy || !deps.canManage()) return;
    const requestedLocationId = locationId;
    const sequence = loadSequence;
    const operation = beginWrite();
    recalculationOperation = operation;
    recalculationText = "Recalculating...";
    levelsError = "";
    renderLevels();
    try {
      const result = await postAutoParRecalculate(deps.apiBaseUrl(), deps.tenantId(), requestedLocationId);
      if (pendingWrite !== operation || sequence !== loadSequence || requestedLocationId !== locationId || !deps.isCurrent()) return;
      if (result.status !== 200 || result.body.summary === undefined) {
        recalculationText = `Recalculation failed. ${messageOf(result.body)}`;
        return;
      }
      const summary = result.body.summary;
      const resultText = recalculationResultText(summary);
      const refreshed = await load();
      if (pendingWrite !== operation || requestedLocationId !== locationId || !deps.isCurrent()) return;
      recalculationText = refreshed ? resultText : `${resultText} Inventory could not be refreshed. Try again.`;
    } catch {
      if (pendingWrite === operation && requestedLocationId === locationId && deps.isCurrent()) recalculationText = "Recalculation could not finish. Try again.";
    } finally {
      if (pendingWrite === operation) {
        pendingWrite = undefined;
        busy = false;
        recalculationOperation = undefined;
        if (requestedLocationId === locationId && deps.isCurrent()) {
          if (recalculationText === "Recalculating...") recalculationText = "Recalculation status is unavailable. Refresh inventory.";
          // QAWALK1-F5. render(), not renderLevels(): load() above redraws the whole card while busy is still true,
          // which draws the on/off switch and the vendor controls disabled. Only the toolbar was redrawn here, so
          // the switch stayed disabled after a successful Recalculate until the screen was reopened.
          render();
        }
      }
    }
  }

  async function toggleLock(item: AutoParItemDto): Promise<void> {
    if (locationId === undefined || busy) return;
    const sequence = loadSequence;
    const operation = beginWrite();
    renderLevels();
    const result = await finishWrite(operation, sequence, putAutoParItemLock(
      deps.apiBaseUrl(),
      deps.tenantId(),
      locationId,
      item.liquorProductId,
      !item.locked,
    ));
    if (sequence !== loadSequence || !deps.isCurrent()) return;
    levelsError = result.status === 200 ? "" : messageOf(result.body);
    await loadItems();
  }

  async function acceptItem(item: AutoParItemDto): Promise<void> {
    if (locationId === undefined || busy || item.heldPar === null) return;
    const sequence = loadSequence;
    const operation = beginWrite();
    renderLevels();
    const result = await finishWrite(operation, sequence, postAutoParAccept(
      deps.apiBaseUrl(),
      deps.tenantId(),
      locationId,
      item.liquorProductId,
      item.heldPar,
    ));
    if (sequence !== loadSequence || !deps.isCurrent()) return;
    levelsError = result.status === 200 ? "" : messageOf(result.body);
    await load();
  }

  // -------------------------------------------------------------------
  // Sheets
  // -------------------------------------------------------------------

  function closeSheet(): void {
    sheetScrim?.remove();
    sheetScrim = undefined;
  }

  function openSheetFrame(
    titleText: string,
    testId: string,
  ): {
    scrim: HTMLElement;
    body: HTMLElement;
    actions: HTMLElement;
    kicker: HTMLElement;
  } {
    closeSheet();
    const scrim = node("div", "scrim ap-scrim");
    scrim.dataset["testid"] = `${testId}-scrim`;
    const sheet = node("div", "sheet admin-confirm-sheet ap-sheet");
    sheet.dataset["testid"] = testId;
    sheet.setAttribute("role", "dialog");
    sheet.setAttribute("aria-modal", "true");
    const titleId = `${testId}-title`;
    sheet.setAttribute("aria-labelledby", titleId);
    const header = node("div", "sheet-header");
    const kicker = node("span", "ap-kicker");
    const title = node("span", "sheet-title", titleText);
    title.id = titleId;
    title.dataset["testid"] = titleId;
    header.append(kicker, title);
    const body = node("div", "sheet-body ap-sheet-body");
    const actions = node("div", "admin-confirm-actions ap-sheet-actions");
    sheet.append(header, body, actions);
    scrim.append(sheet);
    parts.sheetHost.append(scrim);
    sheetScrim = scrim;
    return { scrim, body, actions, kicker };
  }

  function openOffSheet(): void {
    const current = overview;
    if (current === undefined) return;
    const frame = openSheetFrame("Turn off automatic par levels?", `${ID}-off-sheet`);
    let restore = false;
    const options = node("div", "ap-options");
    options.setAttribute("role", "radiogroup");
    options.setAttribute("aria-label", "What happens to the pars");
    const keepSub = `${current.offPrompt.keepCount} pars stay as they are now`;
    const restoreSub = `${current.offPrompt.restoreCount} pars change back, ${current.offPrompt.noEarlierCount} had no earlier par`;
    const buttons: HTMLButtonElement[] = [];
    function option(key: "keep" | "restore", title: string, sub: string): void {
      const button = node("button", "ap-option");
      button.type = "button";
      button.setAttribute("role", "radio");
      button.dataset["testid"] = `${ID}-off-${key}`;
      const dot = node("span", "ap-option-dot");
      const text = node("span", "ap-option-text");
      text.append(node("span", "ap-option-title", title), node("span", "ap-sub", sub));
      button.append(dot, text);
      button.addEventListener("click", () => {
        restore = key === "restore";
        paint();
      });
      buttons.push(button);
      options.append(button);
    }
    option("keep", "Keep current pars", keepSub);
    option("restore", "Put back my earlier pars", restoreSub);
    function paint(): void {
      buttons.forEach((button, index) => {
        const on = (index === 1) === restore;
        button.setAttribute("aria-checked", String(on));
        button.classList.toggle("ap-option-on", on);
      });
    }
    paint();
    frame.body.append(options);
    const cancel = node("button", "btn-secondary", "Cancel");
    cancel.type = "button";
    cancel.dataset["testid"] = `${ID}-off-cancel`;
    cancel.addEventListener("click", closeSheet);
    const confirm = node("button", "btn-danger", "Turn off");
    confirm.type = "button";
    confirm.dataset["testid"] = `${ID}-off-confirm`;
    confirm.addEventListener("click", () => void switchOff(restore));
    frame.actions.append(cancel, confirm);
  }

  interface PaceSheetOptions {
    vendors: AutoParVendorDto[];
    index: number;
    /** True while the switch was just turned on and each vendor is asked in turn. */
    flow: boolean;
    /** Called with the saved vendor row (Vendors screen). */
    onSaved?: (vendor: VendorDto) => void;
    /** Skips the Settings reload (the sheet was opened from another screen). */
    external?: boolean;
  }

  function openPaceSheet(options: PaceSheetOptions): void {
    const vendor = options.vendors[options.index];
    if (vendor === undefined) {
      closeSheet();
      return;
    }
    let draft: PaceDraft = draftFromVendor(vendor);
    let saving = false;
    let error = "";
    const frame = openSheetFrame(`${vendor.name} order pace`, `${ID}-pace-sheet`);
    if (options.flow) {
      frame.kicker.textContent = `Vendor ${options.index + 1} of ${options.vendors.length}`;
    }
    const last = options.index === options.vendors.length - 1;

    function paint(): void {
      frame.body.replaceChildren();
      frame.actions.replaceChildren();

      frame.body.append(node("span", "ap-label", "Order every"));
      const presets = node("div", "ap-chips");
      presets.setAttribute("role", "radiogroup");
      presets.setAttribute("aria-label", "Order every");
      const selected = presetOf(draft);
      for (const weeks of PACE_PRESET_WEEKS) {
        const chip = node("button", "ap-chip", presetLabel(weeks));
        chip.type = "button";
        chip.setAttribute("role", "radio");
        chip.setAttribute("aria-checked", String(selected === weeks));
        chip.dataset["testid"] = `${ID}-preset-${weeks}`;
        chip.addEventListener("click", () => {
          draft = { ...draft, unit: "weeks", amount: weeks };
          paint();
        });
        presets.append(chip);
      }
      const other = node("button", "ap-chip", "Other");
      other.type = "button";
      other.setAttribute("role", "radio");
      other.setAttribute("aria-checked", String(selected === "other"));
      other.dataset["testid"] = `${ID}-preset-other`;
      other.addEventListener("click", () => {
        // Other opens on days, the finer unit, keeping the days it already means.
        draft = withUnit(draft, "days");
        paint();
      });
      presets.append(other);
      frame.body.append(presets);

      const amountRow = node("div", "ap-pace-amount");
      const units = node("div", "ap-seg");
      units.setAttribute("role", "radiogroup");
      units.setAttribute("aria-label", "Unit");
      for (const unit of ["days", "weeks"] as const) {
        const button = node("button", "ap-seg-button", unit === "days" ? "Days" : "Weeks");
        button.type = "button";
        button.setAttribute("role", "radio");
        button.setAttribute("aria-checked", String(draft.unit === unit));
        button.dataset["testid"] = `${ID}-unit-${unit}`;
        button.addEventListener("click", () => {
          draft = withUnit(draft, unit);
          paint();
        });
        units.append(button);
      }
      amountRow.append(
        units,
        stepper({
          testId: `${ID}-amount`,
          label: "Order every",
          value: String(draft.amount),
          unit:
            draft.unit === "weeks"
              ? draft.amount === 1
                ? "week"
                : "weeks"
              : draft.amount === 1
                ? "day"
                : "days",
          canDecrease: draft.amount > 1,
          canIncrease: paceDays(stepAmount(draft, 1)) !== paceDays(draft),
          onStep: (direction) => {
            draft = stepAmount(draft, direction);
            paint();
          },
        }),
      );
      frame.body.append(amountRow);

      frame.body.append(node("span", "ap-label", "Order day (optional)"));
      const days = node("div", "ap-chips");
      days.setAttribute("role", "radiogroup");
      days.setAttribute("aria-label", "Order day");
      const any = node("button", "ap-chip ap-chip-day", "Any");
      any.type = "button";
      any.setAttribute("role", "radio");
      any.setAttribute("aria-checked", String(draft.weekday === null));
      any.dataset["testid"] = `${ID}-day-any`;
      any.addEventListener("click", () => {
        draft = { ...draft, weekday: null };
        paint();
      });
      days.append(any);
      for (const day of WEEKDAY_VALUES) {
        const chip = node("button", "ap-chip ap-chip-day", WEEKDAY_SHORT[day]);
        chip.type = "button";
        chip.setAttribute("role", "radio");
        chip.setAttribute("aria-checked", String(draft.weekday === day));
        chip.dataset["testid"] = `${ID}-day-${day}`;
        chip.addEventListener("click", () => {
          draft = { ...draft, weekday: day as WeekdayValue };
          paint();
        });
        days.append(chip);
      }
      frame.body.append(days);

      frame.body.append(node("span", "ap-label", "Delivery takes"));
      frame.body.append(
        stepper({
          testId: `${ID}-lead`,
          label: "Delivery takes",
          value: String(draft.leadDays),
          unit: draft.leadDays === 1 ? "day" : "days",
          canDecrease: draft.leadDays > LEAD_RANGE_DAYS.min,
          canIncrease: draft.leadDays < LEAD_RANGE_DAYS.max,
          onStep: (direction) => {
            draft = stepLead(draft, direction);
            paint();
          },
        }),
      );

      const covers = node("div", "ap-covers");
      covers.dataset["testid"] = `${ID}-covers`;
      covers.append(
        node("b", null, `${coverageDays(draft)} days`),
        node("span", null, "of use covered by each par"),
      );
      frame.body.append(covers);
      if (error !== "") frame.body.append(errorNode(error));

      const skipOrCancel = node("button", "btn-secondary", options.flow ? "Skip vendor" : "Cancel");
      skipOrCancel.type = "button";
      skipOrCancel.dataset["testid"] = `${ID}-pace-skip`;
      skipOrCancel.disabled = saving;
      skipOrCancel.addEventListener("click", () => {
        if (options.flow && !last) {
          openPaceSheet({ ...options, index: options.index + 1 });
          return;
        }
        closeSheet();
        // The end of the prompt: draw what the server now holds for every vendor asked.
        if (options.flow) void load();
      });
      frame.actions.append(skipOrCancel);
      if (options.flow) {
        const back = node("button", "btn-secondary", "Back");
        back.type = "button";
        back.dataset["testid"] = `${ID}-pace-back`;
        back.disabled = saving || options.index === 0;
        back.addEventListener("click", () =>
          openPaceSheet({ ...options, index: options.index - 1 }),
        );
        frame.actions.append(back);
      }
      const save = node(
        "button",
        "btn-primary",
        options.flow ? (last ? "Done" : "Next vendor") : "Save",
      );
      save.type = "button";
      save.dataset["testid"] = `${ID}-pace-save`;
      save.disabled = saving;
      save.addEventListener("click", () => void savePace());
      frame.actions.append(save);
    }

    async function savePace(): Promise<void> {
      saving = true;
      error = "";
      paint();
      const result = await putVendorOrderPace(deps.apiBaseUrl(), deps.tenantId(), vendor!.id, {
        paceDays: paceDays(draft),
        orderWeekday: draft.weekday,
        leadDays: draft.leadDays,
      });
      if (!deps.isCurrent()) return;
      saving = false;
      if (result.status !== 200 || result.body.vendor === undefined) {
        error = messageOf(result.body);
        paint();
        return;
      }
      options.onSaved?.(result.body.vendor);
      if (options.flow && !last) {
        // Remember what was saved, so Back shows it.
        options.vendors[options.index] = {
          ...vendor!,
          paceDays: result.body.vendor.order_pace_days,
          orderWeekday: result.body.vendor.order_pace_weekday,
          leadDays: result.body.vendor.lead_days,
        };
        openPaceSheet({ ...options, index: options.index + 1 });
        return;
      }
      closeSheet();
      if (options.external !== true) await load();
    }

    paint();
  }

  function inventoryRow(productId: string): AutoParItemDto | undefined {
    return deps.canView() && overview !== undefined && itemsLoaded ? items.find((item) => item.liquorProductId === productId) : undefined;
  }

  return {
    inventoryLocationId: () => locationId,
    inventoryLocationName: () => locations.find((siteLocation) => siteLocation.id === locationId)?.name,
    inventoryPending: () => deps.canView() && locationId !== undefined && !itemsLoaded,
    inventoryRow,
    inventoryMatchesVendor(productId) {
      if (levelsVendorId === undefined) return true;
      const item = inventoryRow(productId);
      if (item === undefined || item.preferredVendorId === undefined) return true;
      return levelsVendorId === "none" ? item.preferredVendorId === null : item.preferredVendorId === levelsVendorId;
    },
    inventoryVendorName(productId) {
      const item = inventoryRow(productId);
      if (item === undefined || item.preferredVendorId === undefined) return undefined;
      if (item.preferredVendorId === null) return "Not set";
      return overview?.vendors.find((vendor) => vendor.id === item.preferredVendorId)?.name;
    },
    inventoryCells(productId) {
      const item = inventoryRow(productId);
      if (item === undefined) return undefined;
      const pieces = [...itemRow(item, deps.canManage() && !busy && item.eligible !== false).children] as HTMLElement[];
      return { auto: pieces[4]!, used: pieces[3]!, lock: pieces[5]! };
    },
    async refresh() {
      errorLine = "";
      await load();
    },
    reset,
    openVendorPace(vendor, onSaved) {
      openPaceSheet({
        vendors: [{ ...vendor, itemCount: 0 }],
        index: 0,
        flow: false,
        onSaved,
        external: true,
      });
    },
    async showLevels(vendorId) {
      if (overview === undefined) await load();
      if (overview === undefined) return;
      if (vendorId !== undefined && levelVendors().some((vendor) => vendor.id === vendorId)) {
        levelsVendorId = vendorId;
      }
      tab = "levels";
      itemsLoaded = false;
      render();
      await loadItems();
    },
  };
}
